import React, { useRef, useCallback, useEffect, useState } from 'react';

const EMOJIS = ['👍', '👎', '✅', '❌', '⚠️', '📌', '📎', '🔥', '💡', '📝', '📦', '🚚', '💰', '🔧', '📞', '⭐'];

interface RichTextInputProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
}

const RichTextInput: React.FC<RichTextInputProps> = ({ value, onChange, placeholder = 'Digite aqui...', minHeight = '60px' }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [showEmojis, setShowEmojis] = useState(false);
  const isInternalChange = useRef(false);

  // Sync external value changes to editor
  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalChange.current = true;
      const html = editorRef.current.innerHTML;
      // Convert <div><br></div> that contenteditable creates to empty
      const cleaned = html === '<br>' || html === '<div><br></div>' ? '' : html;
      onChange(cleaned);
    }
  }, [onChange]);

  const execCmd = useCallback((cmd: string, val?: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    handleInput();
  }, [handleInput]);

  const insertEmoji = useCallback((emoji: string) => {
    editorRef.current?.focus();
    document.execCommand('insertText', false, emoji);
    handleInput();
    setShowEmojis(false);
  }, [handleInput]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'b' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      execCmd('bold');
    } else if (e.key === 'i' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      execCmd('italic');
    } else if (e.key === 'u' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      execCmd('underline');
    }
  }, [execCmd]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    // Verificar se há imagens no clipboard
    const items = e.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          e.preventDefault();
          const file = items[i].getAsFile();
          if (!file) return;
          
          // Limitar tamanho (2MB para imagens inline)
          if (file.size > 2 * 1024 * 1024) {
            alert('⚠️ Imagem muito grande!\nMáximo permitido: 2MB');
            return;
          }
          
          const reader = new FileReader();
          reader.onload = (ev) => {
            const base64 = ev.target?.result as string;
            if (base64 && editorRef.current) {
              editorRef.current.focus();
              const img = `<img src="${base64}" style="max-width:100%;height:auto;border-radius:6px;margin:4px 0;" />`;
              document.execCommand('insertHTML', false, img);
              handleInput();
            }
          };
          reader.readAsDataURL(file);
          return;
        }
      }
    }
    
    // Sem imagem: colar como texto puro
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, [handleInput]);

  return (
    <div className="border border-gray-200 rounded-lg focus-within:ring-2 focus-within:ring-black/30 focus-within:border-gray-800 transition-all bg-white">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1 border-b border-gray-100 bg-gray-50 rounded-t-lg">
        <button
          type="button"
          title="Negrito (Ctrl+B)"
          onMouseDown={(e) => { e.preventDefault(); execCmd('bold'); }}
          className="p-1 px-1.5 hover:bg-gray-200 rounded text-xs font-bold text-gray-700"
        >
          B
        </button>
        <button
          type="button"
          title="Itálico (Ctrl+I)"
          onMouseDown={(e) => { e.preventDefault(); execCmd('italic'); }}
          className="p-1 px-1.5 hover:bg-gray-200 rounded text-xs italic text-gray-700"
        >
          I
        </button>
        <button
          type="button"
          title="Sublinhado (Ctrl+U)"
          onMouseDown={(e) => { e.preventDefault(); execCmd('underline'); }}
          className="p-1 px-1.5 hover:bg-gray-200 rounded text-xs underline text-gray-700"
        >
          U
        </button>
        <div className="w-px h-4 bg-gray-300 mx-0.5" />
        <button
          type="button"
          title="Lista com marcadores"
          onMouseDown={(e) => { e.preventDefault(); execCmd('insertUnorderedList'); }}
          className="p-1 px-1.5 hover:bg-gray-200 rounded text-xs text-gray-700"
        >
          • Lista
        </button>
        <button
          type="button"
          title="Lista numerada"
          onMouseDown={(e) => { e.preventDefault(); execCmd('insertOrderedList'); }}
          className="p-1 px-1.5 hover:bg-gray-200 rounded text-xs text-gray-700"
        >
          1. Lista
        </button>
        <div className="w-px h-4 bg-gray-300 mx-0.5" />
        <div className="relative">
          <button
            type="button"
            title="Inserir emoji"
            onClick={() => setShowEmojis(!showEmojis)}
            className="p-1 px-1.5 hover:bg-gray-200 rounded text-xs text-gray-700"
          >
            😀
          </button>
          {showEmojis && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-50 grid grid-cols-8 gap-1" style={{ width: '220px' }}>
              {EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => insertEmoji(emoji)}
                  className="p-1 hover:bg-gray-100 rounded text-base cursor-pointer"
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={() => setShowEmojis(false)}
        data-placeholder={placeholder}
        className="px-3 py-2 text-sm outline-none rounded-b-lg overflow-auto rich-text-editor"
        style={{ minHeight }}
        suppressContentEditableWarning
      />

      <style>{`
        .rich-text-editor:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        .rich-text-editor ul { list-style: disc; padding-left: 1.5em; margin: 0.25em 0; }
        .rich-text-editor ol { list-style: decimal; padding-left: 1.5em; margin: 0.25em 0; }
        .rich-text-editor li { margin: 0.1em 0; }
        .rich-text-editor img { max-width: 100%; height: auto; border-radius: 6px; margin: 4px 0; cursor: pointer; }
        .rich-text-editor img:hover { opacity: 0.9; box-shadow: 0 0 0 2px #3b82f6; }
      `}</style>
    </div>
  );
};

export default RichTextInput;
